import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List, Star, Calendar, Tag, BarChart3, ExternalLink, Image as ImageIcon } from 'lucide-react'; // Added ExternalLink, ImageIcon
import DashboardCard from './admin/DashboardCard';
import { useNavigate } from 'react-router-dom';
import { getApiUrl, getSupabaseHeaders } from '../utils/api';

const Gallery = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [filteredDashboards, setFilteredDashboards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState('all'); // Retained for potential future use or if logic is complex
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12); // Keep pagination logic

  useEffect(() => {
    fetchDashboards();
    fetchCategories();
  }, []);

  useEffect(() => {
    let tempFiltered = [...dashboards];

    // Search filter (simplified to title and category for now as per new card design)
    if (searchTerm) {
      tempFiltered = tempFiltered.filter(dashboard =>
        dashboard.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dashboard.category && dashboard.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dashboard.toolsUsed && dashboard.toolsUsed.some(tool => tool.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    // Category filter
    if (selectedCategory) {
      tempFiltered = tempFiltered.filter(dashboard => dashboard.category === selectedCategory);
    }

    // Sort (can be simplified or adjusted based on new display fields)
    tempFiltered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdDate) - new Date(a.createdDate);
        case 'oldest':
          return new Date(a.createdDate) - new Date(b.createdDate);
        case 'title':
          return a.title.localeCompare(b.title);
        // Add other sorting options if needed
        default:
          return 0;
      }
    });

    setFilteredDashboards(tempFiltered);
    setCurrentPage(1); // Reset to first page on filter change
  }, [dashboards, searchTerm, selectedCategory, sortBy]);

  const fetchDashboards = async () => {
    try {
      setLoading(true);
      // Ensure your API returns 'toolsUsed' as an array of strings for each dashboard
      const response = await fetch(getApiUrl('dashboards?limit=100'), {
        method: 'GET',
        headers: {
          ...getSupabaseHeaders(),
          'Accept': 'application/json'
        }
      }); 
      if (response.ok) {
        const responseData = await response.json();
        // Handle the response structure from Supabase Edge Function
        const dashboardsArray = responseData.data || responseData;
        
        // Ensure dashboardsArray is actually an array before calling map
        if (Array.isArray(dashboardsArray)) {
          // Normalize field names to handle both camelCase and lowercase variations
          const normalizedDashboards = dashboardsArray.map(d => ({
            ...d,
            toolsUsed: d.toolsUsed || d.toolsused || d['toolsUsed'] || [],
            // Handle PowerBI link field variations
            powerBIEmbedLink: d.powerBIEmbedLink || d.powerbiembedlink || d['powerBIEmbedLink'],
            // Handle main image field variations
            mainImage: d.mainImage || d.mainimage || d['mainImage'],
            // Handle additional images field variations
            additionalImages: d.additionalImages || d.additionalimages || d['additionalImages'] || [],
            // Handle created date field variations
            createdDate: d.createdDate || d.createddate || d['createdDate'] || d.createdAt || d.createdat || d['createdAt']
          }));
          setDashboards(normalizedDashboards);
        } else {
          console.error('Invalid dashboard data format received:', dashboardsArray);
          setError('Invalid dashboard data format received from server');
        }
      } else {
        const errorText = `Failed to fetch dashboards: ${response.status} ${response.statusText}`;
        console.error(errorText);
        setError(errorText);
      }
    } catch (err) {
      const errorMessage = `Network error: ${err.message}. Please try again.`;
      console.error(errorMessage, err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => { // Keep if categories are still used for filtering
    try {
        const response = await fetch(getApiUrl('dashboards/categories'), {
        method: 'GET',
        headers: {
          ...getSupabaseHeaders(),
          'Accept': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        
        // Handle different response structures for categories
        const categoriesArray = data.data?.categories || data.categories || data.data || [];
        
        // Ensure we have an array of categories
        if (Array.isArray(categoriesArray)) {
          setCategories(categoriesArray);
        } else {
          console.error('Invalid categories data format received:', categoriesArray);
          setCategories([]);
        }
      } else {
        const errorText = `Failed to fetch categories: ${response.status} ${response.statusText}`;
        console.error(errorText);
        setError(errorText);
      }
    } catch (err) {
      const errorMessage = `Network error when fetching categories: ${err.message}. Please try again.`;
      console.error(errorMessage, err);
      setError(errorMessage);
    }
  };

  const handleViewDashboard = (slug) => {
    navigate(`/dashboard/${slug}`);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('newest');
  };

  // Pagination logic remains the same
  const totalPages = Math.ceil(filteredDashboards.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDashboards = filteredDashboards.slice(startIndex, startIndex + itemsPerPage);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05, // Faster stagger for cards
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">Loading dashboards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg mb-4">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
          <button
            onClick={fetchDashboards}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-800 dark:text-gray-100">
      {/* Hero Section */}
      <section className="pt-16">
        <div className="py-12 bg-white dark:bg-gray-900">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-4"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">Dashboard Gallery</h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Discover our comprehensive collection of interactive business dashboards and data visualizations built with Power BI and Excel.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <main className="bg-gray-50 dark:bg-gray-950">
        <div className="w-full px-4 sm:px-8 md:px-16 lg:px-24 py-5">
        {/* Filters and Controls - Single Row */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-6 md:mb-8 bg-white dark:bg-gray-800 py-4 px-4 sm:py-6 sm:px-6 shadow-lg rounded-xl border border-gray-200 dark:border-gray-700"
        >
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 md:gap-6">
            {/* Search Input */} 
            <div className="relative flex-grow w-full md:w-auto">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" size={20} />
              <input
                type="text"
                placeholder="Search dashboards..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 h-10 sm:h-12 transition-colors text-sm sm:text-base"
              />
            </div>

            {/* Category Filter Dropdown */} 
            <div className="relative w-full sm:w-auto sm:min-w-[140px]">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 h-10 sm:h-12 appearance-none pr-8 transition-colors text-sm sm:text-base"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" size={18} />
            </div>

            {/* Sort By Dropdown */} 
            <div className="relative w-full sm:w-auto sm:min-w-[120px]">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 h-10 sm:h-12 appearance-none pr-8 transition-colors text-sm sm:text-base"
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="title">Title (A-Z)</option>
              </select>
              <BarChart3 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" size={18} /> {/* Changed icon */} 
            </div>

            {/* View Mode Toggle */} 
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden h-10 sm:h-12 w-full sm:w-auto">
              <button
                onClick={() => setViewMode('grid')}
                className={`flex-1 sm:flex-none p-2 sm:p-3 transition-colors ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                title="Grid View"
              >
                <Grid size={18} className="sm:w-5 sm:h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex-1 sm:flex-none p-2 sm:p-3 transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                title="List View"
              >
                <List size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
          {/* Results info */} 
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-3 sm:mt-4 text-center">Showing {paginatedDashboards.length} of {filteredDashboards.length} dashboards</p>
        </motion.div>

        {/* Dashboards Display */} 
        {filteredDashboards.length > 0 ? (
          <motion.div
            key={viewMode} // Re-trigger animation on view mode change
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className={viewMode === 'grid' 
              ? "grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
              : "space-y-4 sm:space-y-6" 
            }
          >
            {paginatedDashboards.map(dashboard => (
              <motion.div key={dashboard.id || dashboard.slug} variants={itemVariants} className="h-full">
                {viewMode === 'grid' ? (
                  <GalleryCard dashboard={dashboard} onView={() => handleViewDashboard(dashboard.slug)} />
                ) : (
                  <ListCard dashboard={dashboard} onView={() => handleViewDashboard(dashboard.slug)} />
                )}
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <Search size={48} className="mx-auto text-gray-400 dark:text-gray-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">No Dashboards Found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try adjusting your search or filter criteria.</p>
            {(searchTerm || selectedCategory) && 
              <button 
                onClick={clearFilters} 
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Clear Filters
              </button>
            }
          </div>
        )}

        {/* Pagination Controls - Simplified */} 
        {totalPages > 1 && (
          <div className="mt-12 flex justify-center items-center space-x-2">
            <button 
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(totalPages).keys()].map(num => (
              <button 
                key={num + 1} 
                onClick={() => setCurrentPage(num + 1)} 
                className={`px-4 py-2 border rounded-md transition-colors ${currentPage === num + 1 ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {num + 1}
              </button>
            ))}
            <button 
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

// New GalleryCard Component
const GalleryCard = ({ dashboard, onView }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="rounded-xl overflow-hidden h-full flex flex-col group relative transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onView}
      whileHover={{ 
        scale: 1.05,
        y: -2,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="aspect-[16/10] overflow-hidden relative">
        <img 
          src={dashboard.mainImage || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=60'} 
          alt={dashboard.title} 
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=60'; }} // Fallback image
        />
        {isHovered && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 flex items-center justify-center"
          >
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
              // onClick={(e) => { e.stopPropagation(); onView(); }} // Stop propagation if card itself is clickable
            >
              <ExternalLink size={16} /> View Report
            </button>
          </motion.div>
        )}
      </div>
      <div className="p-4 md:p-5 flex-grow flex flex-col justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 truncate" title={dashboard.title}>{dashboard.title}</h3>
          {/* Display Tools Used as Badges */} 
          {dashboard.toolsUsed && dashboard.toolsUsed.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {dashboard.toolsUsed.slice(0, 3).map(tool => ( // Show max 3 tools
                <span 
                  key={tool} 
                  className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full font-medium"
                >
                  {tool}
                </span>
              ))}
            </div>
          )}
        </div>
        {/* Optional: Add category or date if needed, but keeping it minimal as requested */}
        {/* <p className="text-xs text-gray-500 dark:text-gray-400"><Tag size={12} className="inline mr-1"/>{dashboard.category}</p> */}
      </div>
    </motion.div>
  );
};

// New ListCard Component
const ListCard = ({ dashboard, onView }) => {
  return (
    <motion.div 
      className="rounded-xl overflow-hidden flex items-center gap-3 md:gap-6 p-3 md:p-5 group transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-blue-900/20 border border-gray-200 dark:border-gray-600 hover:border-blue-200 dark:hover:border-blue-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30"
      whileHover={{ 
        scale: 1.02,
        y: -2,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="w-16 h-16 md:w-32 md:h-32 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700">
        <img 
          src={dashboard.mainImage || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=200&q=60'} 
          alt={dashboard.title} 
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=200&q=60'; }}
        />
      </div>
      <div className="flex-grow min-w-0">
        <h3 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2 truncate" title={dashboard.title}>{dashboard.title}</h3>
        {/* Display Tools Used as Badges */} 
        {dashboard.toolsUsed && dashboard.toolsUsed.length > 0 && (
          <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2 sm:mb-3">
            {dashboard.toolsUsed.slice(0, 3).map(tool => ( // Show max 3 tools in mobile list view
              <span 
                key={tool} 
                className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full font-medium"
              >
                {tool}
              </span>
            ))}
          </div>
        )}
        {/* Optional: Category or other info */}
        {/* <p className="text-sm text-gray-500 dark:text-gray-400 mb-3"><Tag size={14} className="inline mr-1"/>{dashboard.category}</p> */}
      </div>
      <motion.button 
          onClick={onView} 
          className="ml-auto flex-shrink-0 px-2 py-1.5 md:px-4 md:py-2 rounded-lg md:rounded-xl text-xs md:text-sm font-semibold flex items-center gap-1 md:gap-2 whitespace-nowrap transition-all duration-300 shadow-sm hover:shadow-md bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 border border-blue-500 hover:border-blue-400"
          whileHover={{ 
            scale: 1.05,
            transition: { type: "spring", stiffness: 400, damping: 25 }
          }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            whileHover={{
              rotate: 5,
              transition: { type: "spring", stiffness: 300, damping: 20 }
            }}
          >
            <ExternalLink size={14} className="md:w-4 md:h-4" />
          </motion.div>
          <span className="hidden sm:inline">View Report</span>
          <span className="sm:hidden">View</span>
        </motion.button>
    </motion.div>
  );
};

export default Gallery;